import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { Subscription, Observable, forkJoin } from 'rxjs';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import { FormFieldConfig } from '@shared/interfaces/config';
import { ValidationObserver } from 'vee-validate';
import { set, get } from 'lodash';
import { ApiRouteType } from '@core/interfaces/core-services';

@Component({
  name: 'bpc-entity-form-provider',
  components: {
    'validation-observer': ValidationObserver,
  },
})
export default class EntityFormProvider<T = AnyEntity> extends Vue {
  @Prop(String) readonly entity: string;
  @Prop([String, Number]) readonly entityId: string | number;
  @Prop(String) readonly savePath: string;
  @Prop(String) readonly searchPath: string;
  @Prop(String) readonly configPath: string;
  @Prop(Boolean) readonly fetchEntity: boolean;
  @Prop(String) readonly fetchPath: string;
  @Prop(String) readonly mode: string;
  @Prop(String) readonly configFileName: string;

  @Prop({ type: String, default: 'default' }) readonly formConfigName: string;
  @Prop(Object) readonly value: AnyEntity;
  @Prop({ type: Object, default: null }) readonly presetDataToInitialize: AnyEntity;
  // This field is used to save the filters values of field to manage in backend if necessary to relate the entity model with other.
  @Prop([String, Object, Array]) readonly extraData: string | AnyEntity;

  @Prop(Boolean) readonly withDraft: boolean;
  @Prop({ type: String, default: null }) readonly apiRouteType: ApiRouteType;

  @Prop({ type: String, default: 'global.generic.successful_action' })
  readonly createdSuccessText: string;
  @Prop({ type: String, default: 'global.generic.successful_action' })
  readonly updatedSuccessText: string;
  @Prop({ type: String, default: 'global.generic.error_form_action' })
  readonly errorMessageText: string;
  @Prop({
    type: Function,
    default: (entityCopy: T): T => {
      return entityCopy;
    },
  })
  readonly actionBeforePrepareToSave: (entityCopy: T) => T;
  @Prop({
    type: Function,
    default: (entityCopy: T): T => {
      return entityCopy;
    },
  })
  readonly actionAfterPrepareToSave: (entityCopy: T) => T;

  $refs!: {
    formProvider: InstanceType<typeof EntityFormProvider> & {
      saveEntity(formModel: AnyEntity): void;
      onMediaFileDelete(): void;
    };
  };

  loading = true;
  loadingConfig = true;
  saving = false;
  search = false;

  state = null;

  entitySubscription: Subscription;
  entityConfigSubscription: Subscription;
  saveEntitySubscription: Subscription;
  searchEntitySubscription: Subscription;
  saveEntityObservable: Observable<BPCGenericApiResponse>;
  searchEntityObservable: Observable<BPCGenericApiResponse>;
  deleteMediaFileObservable: Observable<BPCGenericApiResponse>;

  fieldsObject: Record<string, FormFieldConfig> = {};
  hasMedia: boolean = false;
  withShowMoreButton: boolean = false;
  rawModel: AnyEntity = {};
  errorsObject: object = {};
  disableTag: string = null;
  disabled: boolean = true;
  groupByTabs: boolean = false;
  tabs: [] = [];
  lazyTabs: [] = [];
  conditionalTabs: [] = [];

  public get fields(): FormFieldConfig[] {
    return Object.values(this.fieldsObject).sort(this.compareByOrder);
  }

  public get model(): AnyEntity {
    return this.value;
  }

  public set model(val: AnyEntity) {
    this.$emit('input', val);
  }

  public get fetchEntityPath() {
    if (this.fetchPath) {
      return this.fetchPath;
    } else if (this.entityId) {
      return `${this.$bpcApiRouteHelper.getEntityPath(this.entity, 'raw')}/${this.entityId}`;
    }

    return this.$bpcApiRouteHelper.getEntityPath(this.entity, 'raw');
  }

  public get resolvedConfigFileName(): string {
    return this.configFileName ? this.configFileName : this.entity;
  }

  public get saveEntityPath(): string {
    if (this.savePath) {
      return this.savePath;
    } else if (this.entityId) {
      return `${this.$bpcApiRouteHelper.getEntityPath(this.entity)}/${this.entityId}`;
    }

    return this.$bpcApiRouteHelper.getEntityPath(this.entity);
  }

  public get searchEntityPath(): string {
    if (this.searchPath) {
      return this.searchPath;
    }
    return `${this.$bpcApiRouteHelper.getSearchBookingPath(this.entity)}/search`;
  }

  public get entityFormConfigSlug(): string {
    return this.configPath
      ? this.configPath
      : `${this.resolvedConfigFileName}.forms.${this.formConfigName}`;
  }

  public compareByOrder(a: FormFieldConfig, b: FormFieldConfig): number {
    if (a.order < b.order) {
      return -1;
    }
    if (a.order > b.order) {
      return 1;
    }
    return 0;
  }

  public fetchEntityAndConfig() {
    this.loading = true;
    this.loadingConfig = true;

    const initialization: {
      config: Observable<BPCGenericApiResponse>;
      entity?: Observable<BPCGenericApiResponse>;
    } = {
      config: this.getConfigObservable(),
    };

    if (this.fetchEntity) {
      initialization.entity = this.getEntityObservable();
    }

    if (this.entitySubscription) {
      this.entitySubscription.unsubscribe();
    }
    if (this.entityConfigSubscription) {
      this.entityConfigSubscription.unsubscribe();
    }

    this.entitySubscription = forkJoin(initialization).subscribe(
      (res: { config: BPCGenericApiResponse; entity?: BPCGenericApiResponse }) => {
        this.manageConfigResponse(res.config);
        this.$emit('config-loaded', res.config.data);

        if (res.entity) {
          this.manageEntityResponse(res.entity);
          this.model = this.$bpcModelFactory.getInitialFormModel(
            res.config.data.fields,
            this.entityId,
            res.entity.data,
            this.extraData,
          );
        } else {
          this.model = this.$bpcModelFactory.getInitialFormModel(
            res.config.data.fields,
            this.entityId,
            this.presetDataToInitialize,
            this.extraData,
          );
        }

        this.loading = false;
        this.loadingConfig = false;

        this.$nextTick().then(() => {
          this.$emit('model-loaded', this.model);
        });
      },
      error => {
        console.log(error);
        this.loading = false;
        this.loadingConfig = false;
      },
    );
  }

  public created(): void {
    this.fetchEntityAndConfig();
  }

  manageConfigResponse(res: BPCGenericApiResponse) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.fieldsObject = res.data.fields;
    this.hasMedia = res.data.hasMedia;
    this.withShowMoreButton = res.data.withShowMoreButton;
    this.groupByTabs = res.data.groupByTabs;
    this.disableTag = res.data.disableTag || null;
    this.disabled =
      res.data.disabled === 'false' ||
      res.data.disabled === false ||
      res.data.disabled === '0' ||
      res.data.disabled === 0
        ? false
        : true;
    this.tabs = res.data.tabs;
    this.lazyTabs = res.data.lazyTabs;
    this.conditionalTabs = res.data.conditionalTabs;
  }

  manageEntityResponse(res: BPCGenericApiResponse) {
    this.rawModel = res.data;
  }

  getConfigObservable() {
    return this.$bpcConfig.getEntityConfig(this.entityFormConfigSlug, this.configPath);
  }

  getEntityObservable() {
    return this.getBPCRequestService().get(this.fetchEntityPath);
  }

  public async saveEntity(entity: AnyEntity): Promise<BPCGenericApiResponse> {
    console.log(this.apiRouteType);
    return new Promise((resolve, reject) => {
      this.saving = true;
      let successText = null;

      if (this.saveEntitySubscription) {
        this.saveEntitySubscription.unsubscribe();
      }
      let entityCopy = { ...entity };
      entityCopy = this.prepareToSave(entityCopy);

      if (this.entityId) {
        successText = this.updatedSuccessText;
        if (this.hasMedia) {
          const formData = this.convertModelToFormData(entityCopy, 'put');
          this.saveEntityObservable = this.$bpcApi.putWithMedia(this.saveEntityPath, formData);
        } else {
          this.saveEntityObservable = this.getBPCRequestService().put(
            this.saveEntityPath,
            entityCopy,
            null,
            this.apiRouteType,
            this.withDraft,
          );
        }
      } else {
        successText = this.createdSuccessText;
        if (this.hasMedia) {
          const formData = this.convertModelToFormData(entityCopy);
          this.saveEntityObservable = this.$bpcApi.postWithMedia(this.saveEntityPath, formData);
        } else {
          this.saveEntityObservable = this.getBPCRequestService().post(
            this.saveEntityPath,
            entityCopy,
            null,
            this.apiRouteType,
            this.withDraft,
          );
        }
      }

      this.saveEntitySubscription = this.saveEntityObservable.subscribe(
        (res: BPCGenericApiResponse) => {
          this.$bpcMessage.showSuccessMessage({ text: successText });
          this.$emit('entity-saved', res);
          this.saving = false;
          resolve(res);
        },
        error => {
          // NOTE not sure what to do, this is managed in the interceptor
          // this.$bpcMessage.showErrorMessage({ text: this.errorMessageText });
          this.resetErrorObject();
          this.manageSaveErrors(error);
          this.$emit('entity-error-saving', error);
          this.saving = false;
          reject(error);
        },
      );
    });
  }

  public searchEntity(entity: AnyEntity): void {
    this.search = true;

    if (this.searchEntitySubscription) {
      this.searchEntitySubscription.unsubscribe();
    }
    let entityCopy = { ...entity };
    entityCopy = this.prepareToSave(entityCopy);

    this.searchEntityObservable = this.getBPCRequestService().post(
      this.searchEntityPath,
      entityCopy,
    );

    this.searchEntitySubscription = this.searchEntityObservable.subscribe(
      (res: BPCGenericApiResponse) => {
        this.$bpcMessage.showSuccessMessage({ text: this.createdSuccessText });
        this.manageEntityResponse(res);
        // this.model = this.getInitialFormModel(this.fieldsObject, res.data);
        this.model = this.$bpcModelFactory.getInitialFormModel(
          this.fieldsObject,
          this.entityId,
          res.data,
          this.extraData,
        );
        this.$emit('entity-searched', res);
        this.saving = false;
      },
      error => {
        this.$bpcMessage.showErrorMessage({ text: this.errorMessageText });
        this.resetErrorObject();
        this.manageSaveErrors(error);
        this.saving = false;
      },
    );
  }

  resetErrorObject() {
    this.errorsObject = {};
    this.$emit('validation-errors-changed', this.errorsObject);
  }

  manageSaveErrors(error) {
    console.log(error.response.data);
    let change = false;
    if (error.response.status === 422) {
      if (error.response.data.errors) {
        // this.errorsObject = error.response.data.errors;
        Object.keys(error.response.data.errors).forEach(field => {
          const errorField = {
            error: error.response.data.errors[field][0],
            state: false,
            veeValidation: false,
          };
          this.$set(this.errorsObject, field, errorField);
          change = true;
          // if (this.fieldsObject[field]) {
          //   this.$set(this.fieldsObject[field], 'error', error.response.data.errors[field][0]);
          //   this.$set(this.fieldsObject[field], 'state', false);
          //   this.$set(this.fieldsObject[field], 'veeValidation', false);
          // }
        });
      }
    } else if (error.response.status === 420) {
      this.$emit('validation-warning', error);
    }

    if (change) {
      this.$emit('validation-errors-changed', this.errorsObject);
    }
  }

  public restartState(field) {
    if (this.errorsObject[field]) {
      this.$set(this.errorsObject[field], 'error', null);
      this.$set(this.errorsObject[field], 'state', false);
      this.$set(this.errorsObject[field], 'veeValidation', false);
    }
  }

  public getFieldsByTag(tag: string, tab: string = 'default') {
    return this.fields
      .filter((field: FormFieldConfig) => {
        return this.groupByTabs
          ? field.tags.includes(tag) && field.tab === tab
          : field.tags.includes(tag);
      })
      .sort(this.compareByOrder);
  }

  public getFieldsWithAllTags(tags: string[], tab: string = 'default') {
    return this.fields
      .filter((field: FormFieldConfig) => {
        let res = true;
        tags.forEach(tag => {
          if (!field.tags.includes(tag)) {
            res = false;
          }
        });
        if (this.groupByTabs && field.tab !== tab) {
          res = false;
        }
        return res;
      })
      .sort(this.compareByOrder);
  }

  public getFieldsWithAnyTag(tags: string[], tab: string = 'default') {
    return this.fields
      .filter((field: FormFieldConfig) => {
        const tagsInIncludeArray = field.tags.filter(tag => {
          return tags.includes(tag);
        });
        if (this.groupByTabs && field.tab !== tab) {
          return false;
        } else {
          return !!tagsInIncludeArray.length;
        }
      })
      .sort(this.compareByOrder);
  }

  public getFieldsWithoutTags(tags: string[] | null = null, tab: string = 'default') {
    if (!tags) {
      return this.fields
        .filter((field: FormFieldConfig) => {
          return this.groupByTabs
            ? field.tab === tab && (!field.tags || !field.tags.length)
            : !field.tags || !field.tags.length;
        })
        .sort(this.compareByOrder);
    } else {
      return this.fields
        .filter((field: FormFieldConfig) => {
          const tagsInExcludeArray = field.tags.filter(tag => {
            return tags.includes(tag);
          });

          if (this.groupByTabs && field.tab !== tab) {
            return false;
          } else {
            return !tagsInExcludeArray.length;
          }
          //   console.log('tagsInExcludeArray', tagsInExcludeArray);
        })
        .sort(this.compareByOrder);
    }
  }

  protected prepareToSave(entityCopy: any) {
    entityCopy = this.actionBeforePrepareToSave(entityCopy);
    Object.values(this.fieldsObject).forEach(field => {
      if (
        (field.inputType == 'entity-select' || field.inputType == 'entity-select-table') &&
        !field.selectValue
      ) {
        let fieldPath = field.fieldPath;
        if (entityCopy[field.fieldPath]) {
          let fieldValue = null;
          if (field.selectMultiple) {
            fieldValue = entityCopy[field.fieldPath].map(function(element) {
              if (typeof element === 'object' && element !== null) {
                if (field.taggable && field.pushTags) {
                  return element.id ? element.id : element.name;
                }
                return element.id;
              } else {
                return element;
              }
            });
          } else {
            fieldValue = entityCopy[field.fieldPath]['id'];
            if (field.selectForeignKey) {
              fieldPath = field.selectForeignKey;
            } else {
              fieldPath = this.camelToSnake(fieldPath) + '_id';
            }
          }
          set(entityCopy, fieldPath, fieldValue);
        } else {
          fieldPath = this.camelToSnake(fieldPath) + '_id';
          if (!field.selectMultiple && entityCopy[fieldPath]) {
            entityCopy[fieldPath] = null;
          }
        }
      } else if (field.inputType == 'entity-table' && this.value.id) {
        entityCopy[field.fieldPath] = null;
      } else if (
        field.splitBeforeSend &&
        entityCopy[field.fieldPath] &&
        this.checkCondition(field, entityCopy)
      ) {
        // Si the field config has splitBeforeSend to true, then we split the value of this prop and put in the root of the model
        for (const key in entityCopy[field.fieldPath]) {
          if (entityCopy[field.fieldPath][key]) {
            entityCopy[key] = entityCopy[field.fieldPath][key];
          }
        }
      }
    });
    entityCopy = this.actionAfterPrepareToSave(entityCopy);
    return entityCopy;
  }

  private camelToSnake(string) {
    return string
      .replace(/[\w]([A-Z])/g, function(m) {
        return m[0] + '_' + m[1];
      })
      .toLowerCase();
  }

  public setFilters(field: any, model: AnyEntity) {
    if (field.filters) {
      field.filters.forEach(filter => {
        if (filter['modelField']) {
          set(filter, 'value', model[filter['modelField']]);
        }
      });
    }
  }

  private getBPCRequestService() {
    if (this.mode === 'storage') {
      return this.$bpcStorage;
    } else {
      return this.$bpcApi;
    }
  }

  protected convertModelToFormData(model: any, method: string = null) {
    const formData = new FormData();
    if (method === 'put') {
      formData.append('_method', method);
    }
    return this.buildFormData(model, formData);
  }

  protected buildFormData(data, formData: FormData, parentKey: string = '') {
    if (
      data &&
      typeof data === 'object' &&
      !(data instanceof Date) &&
      !(data instanceof File) &&
      (!Array.isArray(data) || (Array.isArray(data) && data.length > 0))
    ) {
      Object.keys(data).forEach(key => {
        formData = this.buildFormData(
          data[key],
          formData,
          parentKey ? `${parentKey}[${key}]` : key,
        );
      });
    } else {
      const value = data === null ? '' : data;
      formData.append(parentKey, value);
    }
    return formData;
  }

  private checkCondition(fieldConfig: FormFieldConfig, entity) {
    if (fieldConfig.conditionalHide) {
      return entity[fieldConfig.conditionalHide.fieldPath] != fieldConfig.conditionalHide.value;
    }
    return true;
  }

  public getWithShowMoreButton() {
    return this.withShowMoreButton;
  }

  public getGroupByTabs() {
    return this.groupByTabs;
  }

  @Watch('saving')
  onSavingChange() {
    this.$emit('saving-entity', this.saving);
  }
}
